import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { studio } from "@/data/site";
import { MaskLines } from "@/components/common/Reveal";
import { featuredApp, publishedApps, visibleApps } from "@/data/apps";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [0, -5]);
  const haloY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      className="grain relative overflow-hidden pt-28 sm:pt-36 lg:pt-44"
      data-testid="home-hero"
    >
      <motion.div
        aria-hidden
        style={{ y: haloY }}
        className="halo pointer-events-none absolute -top-40 left-1/2 h-[720px] w-[1100px] -translate-x-1/2"
      />
      <div className="shell relative">
        <div className="grid items-end gap-14 lg:grid-cols-[1.15fr_0.85fr] lg:gap-8">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="glass mb-8 inline-flex items-center gap-3 rounded-full px-4 py-2"
            >
              <span
                aria-hidden
                className="h-1.5 w-1.5 animate-pulse rounded-full"
                style={{ background: "var(--accent)" }}
              />
              <span className="font-mono-x text-[11px] uppercase tracking-[0.2em] text-gray-300">
                Independent mobile app studio · {studio.foundedYear}
              </span>
            </motion.div>

            <h1 className="font-display text-4xl font-black leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-[4.6rem]">
              <MaskLines
                lines={["We make small", "mobile apps that", "earn their place."]}
                delay={0.15}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.62, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 max-w-xl text-base leading-relaxed text-gray-400 sm:text-lg"
            >
              {studio.intro}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.74, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/apps"
                data-testid="hero-cta-apps"
                className="group inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium text-[#08090d] transition-transform duration-300 hover:-translate-y-0.5"
                style={{ background: "var(--accent)" }}
              >
                Browse our apps
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              {featuredApp && (
                <Link
                  to={`/apps/${featuredApp.slug}`}
                  data-testid={`hero-app-${featuredApp.slug}-cta`}
                  className="inline-flex items-center gap-2 rounded-full border px-6 py-3.5 text-sm text-white transition-colors duration-300 hover:border-[var(--accent)]"
                  style={{ borderColor: "var(--line)" }}
                >
                  Meet {featuredApp.name}
                </Link>
              )}
            </motion.div>

            <motion.dl
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.9 }}
              className="mt-14 grid max-w-lg grid-cols-3 gap-6 pt-8"
              style={{ borderTop: "1px solid var(--line)" }}
            >
              {[
                { k: publishedApps.length, l: "app live" },
                { k: visibleApps.length, l: "in the studio" },
                { k: "2", l: "platforms" },
              ].map((s) => (
                <div key={s.l}>
                  <dt className="font-display text-2xl font-extrabold text-white sm:text-3xl">
                    {s.k}
                  </dt>
                  <dd className="mt-1 font-mono-x text-[11px] uppercase tracking-[0.16em] text-gray-500">
                    {s.l}
                  </dd>
                </div>
              ))}
            </motion.dl>
          </div>

          {featuredApp?.screenshots?.[0] && (
            <motion.div
              style={{ y: imgY, rotate: imgRotate }}
              initial={{ opacity: 0, scale: 0.94, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.3, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative mx-auto w-full max-w-[320px] lg:max-w-none"
            >
              <div
                className="absolute -inset-10 -z-10 rounded-full blur-3xl"
                style={{ background: "rgba(224,122,95,0.18)" }}
                aria-hidden
              />
              <div
                className="overflow-hidden rounded-[2rem]"
                style={{ border: "1px solid var(--line)", background: "var(--surface)" }}
              >
                <img
                  src={featuredApp.screenshots[0].url}
                  alt={`${featuredApp.name} app screen`}
                  className="block max-h-[64vh] w-full object-cover object-top"
                />
              </div>
              <div className="glass absolute -left-4 bottom-8 hidden rounded-2xl px-4 py-3 sm:block">
                <p className="font-mono-x text-[10px] uppercase tracking-[0.18em] text-gray-400">
                  Featured
                </p>
                <p className="font-display text-sm font-bold text-white">{featuredApp.name}</p>
              </div>
            </motion.div>
          )}
        </div>

        <motion.div
          style={{ opacity: fade }}
          className="flex items-center gap-3 py-14 text-gray-600"
          aria-hidden
        >
          <ArrowDown className="h-4 w-4 animate-bounce" />
          <span className="font-mono-x text-[11px] uppercase tracking-[0.2em]">Scroll</span>
        </motion.div>
      </div>
    </section>
  );
};
